import { useEffect, useState } from "react";
import { Container, Card, Button, Row } from "react-bootstrap";
import CartItemCard from "../components/CartItemCard";
import { Notyf } from "notyf";
import Swal from "sweetalert2";

function Cart() {
  const notyf = new Notyf();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/cart/get-cart`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      if (!data.cart || !data.cart.cartItems) {
        setCartItems([]);
      } else {
        setCartItems(data.cart.cartItems);
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      notyf.error("Error fetching cart items");
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handleCheckOut = () => {
    // fetch
    fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          notyf.error("Failed to checkout");
          throw new Error("Failed to checkout");
        }
        return response.json();
      })
      .then((data) => {
        // console.log("Checkout successful:", data);
        Swal.fire({
          title: "Checkout Successful",
          text: "Your order has been placed successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        setCartItems([]);
      })
      .catch((error) => {
        notyf.error(`Error during checkout: ${error}`);
      });
  };

  const handleClearCart = () => {
    Swal.fire({
      title: "Clear Cart?",
      text: "Are you sure you want to remove all items from your cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, clear cart",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/clear-cart`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to clear cart");
            }
            return response.json();
          })
          .then((data) => {
            setCartItems([]);
            notyf.success("Cart cleared successfully");
          })
          .catch((error) => {
            console.error("Error clearing cart:", error);
            notyf.error("Failed to clear cart");
          });
      }
    });
  };

  return (
    <div className="cart-page-wrapper">
      {/* Enhanced Cart Hero Section */}
      <div className="cart-hero-section">
        <Container>
          <div className="hero-content py-5">
            <div className="hero-background-elements">
              <div className="floating-cart-1">
                <i className="fas fa-shopping-bag"></i>
              </div>
              <div className="floating-cart-2">
                <i className="fas fa-gift"></i>
              </div>
              <div className="floating-cart-3">
                <i className="fas fa-heart"></i>
              </div>
            </div>

            <div className="text-center position-relative">
              <div className="hero-icon-container mx-auto mb-4">
                <div className="icon-wrapper">
                  <i className="fas fa-shopping-cart text-white main-icon"></i>
                </div>
              </div>

              <h1 className="display-4 fw-bold text-white mb-3 hero-title">
                {cartItems.length === 0 ? "Your Shopping Cart" : `Cart Review`}
              </h1>

              <p className="lead text-white-50 mb-4 hero-subtitle">
                {cartItems.length === 0
                  ? "Ready to discover amazing products? Start your shopping journey now!"
                  : `Review your selected items and proceed to secure checkout`}
              </p>

              <div className="hero-divider mx-auto mb-4"></div>

              {cartItems.length === 0 && (
                <Button
                  href="/products"
                  variant="light"
                  size="lg"
                  className="hero-cta-btn px-5 py-3 fw-semibold"
                >
                  <i className="fas fa-shopping-bag me-2"></i>
                  Explore Products
                </Button>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Cart Content */}
      <Container className="py-5">
        {cartItems.length === 0 ? (
          <div className="empty-cart-section">
            <Card className="border-0 shadow-lg text-center">
              <Card.Body className="py-5">
                <div className="empty-cart-icon mb-4">
                  <i className="fas fa-shopping-bag"></i>
                </div>
                <h3 className="fw-bold text-dark mb-3">Your cart is empty</h3>
                <p className="text-muted mb-4 lead">
                  Discover amazing products and start your shopping journey
                </p>
                <Button
                  href="/products"
                  variant="success"
                  size="lg"
                  className="px-5 py-3 fw-semibold"
                  style={{
                    borderRadius: "25px",
                    background:
                      "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    boxShadow: "0 8px 25px rgba(40, 167, 69, 0.3)",
                  }}
                >
                  <i className="fas fa-shopping-bag me-2"></i>
                  Start Shopping
                </Button>
              </Card.Body>
            </Card>
          </div>
        ) : (
          <div className="cart-content-section">
            {/* Cart Items */}
            <Card className="cart-items-card border-0 shadow-lg mb-4">
              <Card.Header className="bg-white border-0 py-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold text-dark mb-0">
                    <i className="fas fa-list me-2 text-success"></i>
                    Cart Items
                  </h4>
                  <Button
                    onClick={handleClearCart}
                    variant="outline-danger"
                    size="sm"
                    className="fw-semibold"
                    style={{ borderRadius: "20px" }}
                  >
                    <i className="fas fa-trash me-2"></i>
                    Clear All
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-4">
                <Row className="g-4">
                  {cartItems.map((item) => (
                    <CartItemCard
                      key={item.id}
                      item={item}
                      fetchCartItems={fetchCartItems}
                    />
                  ))}
                </Row>
              </Card.Body>
            </Card>

            {/* Order Summary */}
            <Card className="order-summary-card border-0 shadow-lg">
              <Card.Header
                className="border-0 py-4"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                }}
              >
                <h4 className="fw-bold text-dark mb-0">
                  <i className="fas fa-calculator me-2 text-success"></i>
                  Order Summary
                </h4>
              </Card.Header>

              <Card.Body className="p-4">
                <div className="summary-details mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">
                      Subtotal ({cartItems.length} items)
                    </span>
                    <span className="fw-semibold">
                      ₱
                      {calculateTotal().toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Delivery Fee</span>
                    <span className="text-success fw-semibold">FREE</span>
                  </div>
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold text-dark">Total Amount</h5>
                    <h4 className="fw-bold text-success">
                      ₱
                      {calculateTotal().toLocaleString("en-PH", {
                        minimumFractionDigits: 2,
                      })}
                    </h4>
                  </div>
                </div>

                <Button
                  onClick={handleCheckOut}
                  variant="success"
                  size="lg"
                  className="w-100 fw-bold py-3"
                  style={{
                    borderRadius: "15px",
                    fontSize: "1.1rem",
                    background:
                      "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    boxShadow: "0 8px 25px rgba(40, 167, 69, 0.3)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <i className="fas fa-credit-card me-2"></i>
                  Proceed to Checkout
                </Button>
              </Card.Body>
            </Card>
          </div>
        )}
      </Container>
    </div>
  );
}

export default Cart;
